import { useEffect, useRef, useState } from "react";
import { Sparkles, Gift, X, CheckCircle2, ShieldAlert } from "lucide-react";
import { listLivePolls, recordPollResponse, type PollWithOptions, type ResolvedReward } from "@/lib/polls/pollsService";
import { useCleanMode, useKeyboardOpen } from "@/lib/uiOverlays";
import { useFocusModePrefs } from "@/lib/mobileOverlayManager";
import { useDangerPlay } from "@/lib/dangerPlayMode";
import { usePollsConfig } from "@/lib/pollsConfig";
import { trackEvent } from "@/lib/audit";
import { useIsMobile } from "@/hooks/use-mobile";
import { pickNextMatchPoll, resolveMatchPhase } from "@/lib/polls/matchPollEngine";
import { toast } from "sonner";
import { useLanguage } from "@/hooks/useLanguage";
import {
  POLL_MAX_PRE_VOTE_HEIGHT_PX,
  POLL_POST_VOTE_TOAST_MS,
} from "@/lib/pr29/pollCompactMode";
import { useRegisterCtaContext } from "@/lib/pr29a/ctaContext";
import { ensureBinaryOptions } from "@/lib/pr29a/pollOptionsFix";
import { setLayerActive } from "@/lib/pr30/zeroOverlapPolicy";
import {
  canDisplay as pollCanDisplay,
  registerAnswer as pollRegisterAnswer,
  postVoteDismissMs,
} from "@/lib/pr35b/pollLifecycleBridge";
import { useDelayedBySignal } from "@/lib/sync/offsetOrchestration";
import { useClaimAttention, useAttentionYield } from "@/hooks/useAttentionFocus";
import {
  canShowPoll,
  notifyPollShown,
  notifyPollDismissed,
  notifyPollVoted,
  notifyPollAttentionYield,
  subscribePollGate,
  POLL_GATE_MIN_VISIBLE_BEFORE_YIELD_MS,
  POLL_GATE_MIN_POST_VOTE_VISIBLE_MS,
} from "@/lib/polls/pollCadenceGate";

const SEEN_KEY = "vs:polls_seen";

function getSeen(): Set<string> {
  if (typeof window === "undefined") return new Set();
  try { return new Set(JSON.parse(window.localStorage.getItem(SEEN_KEY) || "[]") as string[]); }
  catch { return new Set(); }
}
function markSeen(id: string) {
  const s = getSeen(); s.add(id);
  window.localStorage.setItem(SEEN_KEY, JSON.stringify([...s]));
}

/**
 * Live Poll Widget — auto-fetches active polls and renders a single
 * lightweight overlay that the user can dismiss or vote in. Hides
 * automatically during Goal Clean Mode.
 */
export function LivePollWidget({
  country,
  surface = "live",
  inline = false,
}: {
  country?: string;
  surface?: "live" | "halftime" | "rewards" | "help" | "off_day";
  inline?: boolean;
}) {
  const { t } = useLanguage();
  const [queue, setQueue] = useState<PollWithOptions[]>([]);
  const [poll, setPoll] = useState<PollWithOptions | null>(null);
  const [reward, setReward] = useState<ResolvedReward | null>(null);
  const [voted, setVoted] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [shownCount, setShownCount] = useState(0);
  const lastShownAt = useRef(0);
  // P1-POLL-01 — track when the current poll first became visible so we can
  // enforce a minimum visible window before allowing Attention to yield it.
  const pollVisibleSince = useRef(0);
  const votedAt = useRef(0);
  // Force re-evaluation when the global gate state changes (cross-tab / other engines).
  const [, setGateTick] = useState(0);
  useEffect(() => subscribePollGate(() => setGateTick((n) => n + 1)), []);
  const cleanMode = useCleanMode();
  const keyboardOpen = useKeyboardOpen();
  const danger = useDangerPlay();
  const cfg = usePollsConfig();
  const isMobile = useIsMobile();
  const { focusActive, prefs } = useFocusModePrefs();
  const muted = focusActive && prefs.polls === false;

  // PR-29A — announce active poll so the floating CTA can show "Responder encuesta".
  useRegisterCtaContext("poll", !!poll && !voted);
  useEffect(() => {
    const visible = !!poll && !dismissed && !keyboardOpen && !muted;
    setLayerActive("active_poll", visible);
    return () => setLayerActive("active_poll", false);
  }, [poll, dismissed, keyboardOpen, muted]);

  // Fetch + refresh queue periodically so polls keep flowing during the match.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const live = await listLivePolls(country);
      if (cancelled) return;
      const seen = getSeen();
      let fresh = live.filter((p) => !seen.has(p.id));
      // PR-18: keep questions flowing even when admins haven't published polls.
      if (fresh.length === 0) {
        const engine = pickNextMatchPoll({
          minute: 30,
          phase: resolveMatchPhase(30),
          scoreHome: 0,
          scoreAway: 0,
          dangerActive: danger.active,
        });
        if (engine) {
          fresh = [engine];
          void trackEvent("match_poll_generated", { poll_id: engine.id, source: "engine" });
        }
      }
      // Sponsor-first ordering when configured.
      if (cfg.sponsorPriority) {
        fresh.sort((a, b) => (b.sponsor ? 1 : 0) - (a.sponsor ? 1 : 0));
      }
      // PR-35B — drop polls already answered for this match (dedup gate).
      const gated = fresh.filter((p) =>
        pollCanDisplay({ pollType: p.sponsor ? "sponsored" : "live_poll", question: p.question }),
      );
      setQueue(gated);
    };
    void load();
    const id = window.setInterval(load, 20_000);
    return () => { cancelled = true; window.clearInterval(id); };
  }, [country, surface, cfg.sponsorPriority, danger.active]);

  // Queue engine — only one poll visible at a time, paused during danger play.
  useEffect(() => {
    if (poll) return;
    if (shownCount >= cfg.maxPollsPerMatch) return;
    if (cfg.pauseDuringDangerousPlays && (danger.active || cleanMode)) return;
    // P1-POLL-01 — Global cadence gate (min gap, dismiss / vote / yield cooldowns, caps).
    const decision = canShowPoll();
    if (!decision.allowed) {
      // Re-evaluate when the cooldown window closes (or every 5s if unbounded).
      const wait = decision.waitMs ?? 5_000;
      const tid = window.setTimeout(() => setGateTick((n) => n + 1), Math.max(500, wait));
      return () => window.clearTimeout(tid);
    }
    const next = queue[0];
    if (!next) return;
    setQueue((q) => q.slice(1));
    setPoll(ensureBinaryOptions(next));
    setVoted(false);
    setReward(null);
    setDismissed(false);
    setShownCount((c) => c + 1);
    lastShownAt.current = Date.now();
    pollVisibleSince.current = Date.now();
    notifyPollShown();
    void trackEvent("poll_displayed", { poll_id: next.id, sponsor: next.sponsor, surface });
  }, [queue, poll, danger.active, cleanMode, cfg.frequencySec, cfg.maxPollsPerMatch, cfg.pauseDuringDangerousPlays, shownCount, surface]);

  // Pause/resume telemetry when danger play toggles while a poll is visible.
  const prevPaused = useRef(false);
  useEffect(() => {
    if (!poll) return;
    const paused = cfg.pauseDuringDangerousPlays && (danger.active || cleanMode);
    if (paused && !prevPaused.current) {
      void trackEvent("poll_paused_for_danger_play", { poll_id: poll.id, reason: danger.reason ?? "clean_mode" });
      prevPaused.current = true;
    } else if (!paused && prevPaused.current) {
      void trackEvent("poll_resumed", { poll_id: poll.id });
      prevPaused.current = false;
    }
  }, [danger.active, danger.reason, cleanMode, cfg.pauseDuringDangerousPlays, poll]);

  // Phase 1 — gate visibility through the poll signal weight so polls land
  // in sync with the moment they reference, without delaying internal state.
  const pollVisibilityGate = useDelayedBySignal(
    !!poll && !dismissed && !keyboardOpen && !muted,
    "poll",
  );
  // Master attention — poll claims prio 60 while visible and yields entirely
  // to higher priorities (prediction, sponsor, viral, big moment, takeover).
  const yieldToHigher = useAttentionYield("poll");
  useClaimAttention("poll", !!poll && pollVisibilityGate && !yieldToHigher, 10_000);
  // P1-POLL-01 — When Attention forces a yield, do NOT silently discard the
  // poll. Requeue it (so it can be retried later) and apply the global yield
  // cooldown. Enforce a minimum visible window before yielding so the user
  // actually has time to read the question.
  useEffect(() => {
    if (!poll || !yieldToHigher) return;
    const visibleFor = Date.now() - pollVisibleSince.current;
    if (visibleFor < POLL_GATE_MIN_VISIBLE_BEFORE_YIELD_MS) {
      // Too soon — re-check shortly. Attention will likely still be claimed.
      const remaining = POLL_GATE_MIN_VISIBLE_BEFORE_YIELD_MS - visibleFor;
      const tid = window.setTimeout(() => setGateTick((n) => n + 1), remaining);
      return () => window.clearTimeout(tid);
    }
    // Preserve the poll back at the head of the queue and apply yield cooldown.
    const preserved = poll;
    setPoll(null);
    setQueue((q) => (q.some((p) => p.id === preserved.id) ? q : [preserved, ...q]));
    notifyPollAttentionYield();
    void trackEvent("poll_yielded_preserved", { poll_id: preserved.id });
  }, [poll, yieldToHigher]);
  if (!poll || !pollVisibilityGate) return null;
  if (yieldToHigher) return null;

  // Minimized badge during danger play instead of full-on hide.
  const paused = cfg.pauseDuringDangerousPlays && (danger.active || cleanMode);
  if (paused) {
    return (
      <div
        aria-label={t("poll_paused_danger_aria")}
        className={
          inline
            ? "flex w-full items-center gap-1.5 rounded-xl border border-primary/40 bg-background/80 px-3 py-2 text-[10px] font-bold text-primary backdrop-blur"
            : "fixed bottom-24 left-3 z-40 flex items-center gap-1.5 rounded-full border border-primary/40 bg-background/80 px-2.5 py-1 text-[10px] font-bold text-primary backdrop-blur md:bottom-4"
        }
      >
        <ShieldAlert className="h-3 w-3" /> {t("poll_paused_badge")}
      </div>
    );
  }

  const vote = async (optionId: string) => {
    // PR-35B — register the answer in the lifecycle dedup store.
    pollRegisterAnswer({
      pollType: poll.sponsor ? "sponsored" : "live_poll",
      question: poll.question,
    });
    votedAt.current = Date.now();
    notifyPollVoted();
    if (poll.id.startsWith("engine_")) {
      void trackEvent("match_poll_answered", { poll_id: poll.id, option_id: optionId });
      setVoted(true);
      markSeen(poll.id);
      onPollVoted(poll.id);
      return;
    }
    const res = await recordPollResponse({
      poll_id: poll.id,
      option_id: optionId,
      country,
    });
    setVoted(true);
    if (res.reward) setReward(res.reward);
    markSeen(poll.id);
    onPollVoted(poll.id);
  };

  // PR-29: after vote → success toast for 2.5s, then minimize to a badge.
  // The badge stays until the next eligible poll arrives.
  function onPollVoted(pollId: string) {
    // P1-POLL-01 — keep post-vote result visible at least 5s.
    const dismissMs = Math.max(
      POLL_GATE_MIN_POST_VOTE_VISIBLE_MS,
      Math.min(POLL_POST_VOTE_TOAST_MS, postVoteDismissMs()),
    );
    toast.success(t("poll_vote_toast_title"), { description: t("poll_vote_toast_desc"), duration: dismissMs });
    void trackEvent("poll_vote_confirmation_shown", { poll_id: pollId });
    window.setTimeout(() => {
      setMinimized(true);
      void trackEvent("poll_auto_minimized", { poll_id: pollId });
    }, dismissMs);
  }

  if (minimized && poll) {
    return (
      <button
        onClick={() => { setDismissed(true); setMinimized(false); setPoll(null); }}
        aria-label={t("poll_completed_aria")}
        className={
          inline
            ? "flex w-full items-center gap-1.5 rounded-xl border border-primary/40 bg-background/85 px-3 py-2 text-[11px] font-bold text-primary backdrop-blur"
            : "fixed bottom-24 left-3 z-40 flex items-center gap-1.5 rounded-full border border-primary/40 bg-background/85 px-3 py-1 text-[11px] font-bold text-primary backdrop-blur md:bottom-4"
        }
      >
        {t("poll_completed_badge")}
      </button>
    );
  }

  // Safe-zone positioning:
  //  - Mobile: compact card pinned to bottom (above BottomNav), max 110px tall.
  //  - Desktop: bottom-right side panel that never overlaps the radar/field.
  const mobilePos = inline ? "w-full" : `fixed bottom-20 left-3 right-3 mx-auto max-w-md`;
  const desktopPos = inline ? "" : cfg.desktopPosition === "bottom_right"
    ? "md:fixed md:bottom-4 md:right-4 md:left-auto md:max-w-sm md:max-h-none"
    : "md:fixed md:top-24 md:right-4 md:left-auto md:max-w-xs md:max-h-none";
  // PR-29A — cap height so the card never grows beyond 140px before voting,
  // but never clip option buttons via overflow-hidden.
  const compactStyle: React.CSSProperties = !voted
    ? { maxHeight: Math.max(140, POLL_MAX_PRE_VOTE_HEIGHT_PX) }
    : {};
  return (
    <div className={`${mobilePos} ${desktopPos} border-team-overlay shadow-team-identity z-40 flex flex-col rounded-2xl border bg-background/95 p-3 backdrop-blur`} style={compactStyle}>
      <div className="mb-1 flex items-center justify-between">
        <div className="text-team-overlay flex items-center gap-1.5 text-[10px] font-bold uppercase">
          <Sparkles className="h-3 w-3" /> {poll.sponsor ? t("poll_sponsor_label", { sponsor: poll.sponsor }) : t("poll_live_label")}
        </div>
        <button onClick={() => { setDismissed(true); markSeen(poll.id); setPoll(null); }} aria-label={t("poll_close_aria")} className="rounded p-1 hover:bg-accent/40">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className={`mb-2 font-bold ${isMobile ? "text-xs leading-tight" : "text-sm"}`}>{poll.question}</div>
      {!voted ? (
        <div className={`grid gap-1.5 ${isMobile ? "grid-cols-2" : ""}`}>
          {poll.options.map((o) => (
            <button key={o.id} onClick={() => vote(o.id)}
              className="min-h-[36px] rounded-lg border border-border/60 bg-accent/20 px-3 py-2 text-left text-xs font-medium hover:bg-accent/40 active:scale-[0.98]">
              {o.label}
            </button>
          ))}
        </div>
      ) : (
        <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-2 text-xs">
          <div className="flex items-center gap-1.5 font-bold text-emerald-600"><CheckCircle2 className="h-4 w-4" /> {t("poll_thanks_voted")}</div>
          {reward && (
            <div className="mt-2 space-y-1">
              <div className="flex items-center gap-1.5 text-primary"><Gift className="h-3.5 w-3.5" /> {t("poll_reward_label")}: <b>{reward.type}</b> ({reward.value})</div>
              {reward.code && <div className="font-mono text-[11px]">{t("poll_reward_code")}: <b>{reward.code}</b></div>}
              {reward.expiresAt && <div className="text-[10px] text-muted-foreground">{t("poll_reward_expires")}: {reward.expiresAt.slice(0, 10)}</div>}
              <div className="text-[10px] text-muted-foreground">{reward.instructions}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}